
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import DeviceDetail from "@/pages/DeviceDetail";
import ScriptsPage from "@/pages/ScriptsPage";
import CompliancePage from "@/pages/CompliancePage";
import ComplianceBenchmarksPage from "@/pages/ComplianceBenchmarksPage";
import PatchingPage from "@/pages/PatchingPage";
import VulnerabilitiesPage from "@/pages/VulnerabilitiesPage";
import NewPatchPolicyPage from "@/pages/NewPatchPolicyPage";
import GettingStarted from "@/pages/GettingStarted";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import { Toaster } from "@/components/ui/toaster";
import ConfigurationDetail from "./pages/ConfigurationDetail";
import Configurations from "./pages/Configurations";
import NewConfiguration from "./pages/NewConfiguration";
import NotFound from "./pages/NotFound";
import ConfigurationsPage from "./pages/ConfigurationsPage";
import NewProfilePage from "./pages/NewProfilePage";
import EditProfilePage from "./pages/EditProfilePage";
import NewEnrollmentProfilePage from "./pages/NewEnrollmentProfilePage";
import EditEnrollmentProfilePage from "./pages/EditEnrollmentProfilePage";
import EnrollmentProfilesPage from "./pages/EnrollmentProfilesPage";

import PrivateRoute from "./components/PrivateRoute";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import PreferencesPage from "./pages/PreferencesPage";
import Index from "./pages/Index";
import AdminDashboard from "./pages/AdminDashboard";
import RBACPage from "./pages/RBACPage";
import ApplicationsPage from "./pages/ApplicationsPage";
import AddApplicationPage from "./pages/AddApplicationPage";
import ApplicationDetailPage from "./pages/ApplicationDetailPage";
import ProfileDetailPage from "./pages/ProfileDetailPage";
import TagsPage from "./pages/TagsPage";
import TagDetailPage from "./pages/TagDetailPage";
import EnrollmentProfileDetailPage from "./pages/EnrollmentProfileDetailPage";
import PortalPrivateRoute from "./components/PortalPrivateRoute";
import PortalLoginPage from "./pages/portal/PortalLoginPage";
import MyDevicesPage from "./pages/portal/MyDevicesPage";
import DeviceEnrollmentPage from "./pages/portal/DeviceEnrollmentPage";
import PendingApprovalPage from "./pages/portal/PendingApprovalPage";
import TermsAndConditionsPage from "./pages/portal/TermsAndConditionsPage";
import UsersAndRolesPage from "./pages/UsersAndRolesPage";
import UnifiedUserDetailPage from "./pages/UnifiedUserDetailPage";
import LogsPage from "./pages/LogsPage";
import SettingsPage from "./pages/SettingsPage";
import ComingSoonPreviewPage from "./pages/ComingSoonPreviewPage";
import "./App.css";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import ErrorBoundary from "./components/ErrorBoundary";

function App() {
  return (
    <ThemeProvider 
      attribute="class" 
      defaultTheme="light" 
      enableSystem={false}
      disableTransitionOnChange={false}
      storageKey="vite-ui-theme"
    >
      <NotificationProvider>
        <ErrorBoundary>
          <Router>
            <AnalyticsProvider>
              <Routes>
              {/* Main Admin Portal Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/welcome" element={<Index />} />
              <Route path="/coming-soon-preview" element={<ComingSoonPreviewPage />} />
              
              {/* Device Portal Routes */}
              <Route path="/portal" element={<Navigate to="/portal/login" replace />} />
              <Route path="/portal/login" element={<PortalLoginPage />} />
              <Route path="/portal/pending-approval" element={<PendingApprovalPage />} />
              <Route path="/portal/terms" element={<TermsAndConditionsPage />} />
              <Route 
                path="/portal/mydevices" 
                element={
                  <PortalPrivateRoute>
                    <MyDevicesPage />
                  </PortalPrivateRoute>
                } 
              />
              <Route 
                path="/portal/enroll" 
                element={
                  <PortalPrivateRoute>
                    <DeviceEnrollmentPage />
                  </PortalPrivateRoute>
                } 
              />
              
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                <Route index element={<GettingStarted />} />
                <Route path="getting-started" element={<GettingStarted />} />
                <Route path="devices" element={<Dashboard />} />
                <Route path="devices/:id" element={<DeviceDetail />} />
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="admin" element={<AdminDashboard />} />
                <Route path="admin/users-and-roles" element={<UsersAndRolesPage />} />
                <Route path="admin/users-and-roles/:userId" element={<UnifiedUserDetailPage />} />
                <Route path="admin/enrollment-profiles" element={<EnrollmentProfilesPage />} />
                <Route path="admin/enrollment-profiles/new" element={<NewEnrollmentProfilePage />} />
                <Route path="admin/enrollment-profiles/:id" element={<EnrollmentProfileDetailPage />} />
                <Route path="admin/enrollment-profiles/:id/edit" element={<EditEnrollmentProfilePage />} />
                {/* Redirects for backward compatibility */}
                <Route path="admin/rbac" element={<Navigate to="/admin/users-and-roles?tab=roles" replace />} />
                <Route path="admin/directory-users" element={<Navigate to="/admin/users-and-roles?filter=directory" replace />} />
                <Route path="admin/directory-users/:userId" element={<Navigate to="/admin/users-and-roles/:userId" replace />} />
                <Route path="scripts" element={<ScriptsPage />} />
                <Route path="compliance" element={<CompliancePage />} />
                <Route
                  path="compliance/benchmarks"
                  element={<ComplianceBenchmarksPage />}
                />
                <Route path="patching" element={<PatchingPage />} />
                <Route path="patching/new" element={<NewPatchPolicyPage />} />
                <Route path="vulnerabilities" element={<VulnerabilitiesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="configs" element={<Configurations />} />
                <Route path="configs/new" element={<NewConfiguration />} />
                <Route path="configs/:id" element={<ConfigurationDetail />} />
                <Route path="configurations" element={<ConfigurationsPage />} />
                <Route path="configurations/new" element={<NewProfilePage />} />
                <Route path="configurations/:id" element={<ProfileDetailPage />} />
                <Route path="configurations/:id/edit" element={<EditProfilePage />} />
                {/* Backward compatibility redirects for old /profiles routes */}
                <Route path="profiles" element={<Navigate to="/configurations" replace />} />
                <Route path="profiles/new" element={<Navigate to="/configurations/new" replace />} />
                <Route path="profiles/:id" element={<Navigate to="/configurations/:id" replace />} />
                <Route path="profiles/:id/edit" element={<Navigate to="/configurations/:id/edit" replace />} />
                <Route path="profiles/enrollment/new" element={<Navigate to="/admin/enrollment-profiles/new" replace />} />
                <Route path="profiles/enrollment/:id" element={<Navigate to="/admin/enrollment-profiles/:id" replace />} />
                <Route path="profiles/enrollment/:id/edit" element={<Navigate to="/admin/enrollment-profiles/:id/edit" replace />} />
                
                <Route path="applications" element={<ApplicationsPage />} />
                <Route path="applications/add" element={<AddApplicationPage />} />
                <Route path="applications/:id" element={<ApplicationDetailPage />} />
                <Route path="tags" element={<TagsPage />} />
                <Route path="tags/:tagId" element={<TagDetailPage />} />
                <Route path="logs" element={<LogsPage />} />
                <Route path="preferences" element={<PreferencesPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              </Routes>
              <Toaster />
            </AnalyticsProvider>
          </Router>
        </ErrorBoundary>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
